import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { GET_EVENT_OPEN } from "helpers/jiraya_url";
import { getOpenEvent } from "helpers/jiraya_helper";
import {
    EventData,
    UseEventDataReturn,
    DesignSettingsV1,
    MetadataJson,
    DesignDetails,
} from "types/event";
import { createGridFunctions } from "components/Events/utils";
import { useEventStore } from "store/event/eventStore";
import WebFont from "webfontloader";
import { GALLERY_MODE } from "components/Events/enums";
import { basic } from "./defaultTemplates";
import * as Sentry from "@sentry/react";

type LooseDesignSettings = DesignSettingsV1 & Record<string, unknown>;
type LooseMetadataJson = MetadataJson & Record<string, unknown>;
type LooseDesignDetails = DesignDetails & Record<string, unknown>;

type PreviewState = {
    designSettings: LooseDesignSettings;
    metadataJson: LooseMetadataJson | null;
    designDetails: LooseDesignDetails | null;
    bearerToken: string | null;
};

type DesignSettingsMessage =
    | {
        type: "DESIGN_SETTINGS";
        // Sender might send JSON stringified content, or an already-parsed object
        designSettings: string | LooseDesignSettings | null | undefined;
        eventMetaData: string | Record<string, unknown> | null | undefined;
        designDetails?: string | LooseDesignDetails | null | undefined;
        bearerToken?: string | null | undefined;
    }
    | { type: string;[key: string]: unknown };

/**
 * Safely parse JSON string payloads (e.g. from postMessage).
 */
const safeJsonParse = <T,>(value: string): T => JSON.parse(value) as T;

/**
 * Ensure grid helper functions exist (postMessage JSON won't preserve functions).
 */
const withGridFunctions = (settings: LooseDesignSettings): LooseDesignSettings => {
    if (!settings?.gridStyle) return settings;
    return {
        ...settings,
        // galleryProps is widened here because we intentionally attach helper functions
        // (spacing/columns/targetRowHeight) that aren't part of the API `GalleryProps` type.
        galleryProps: {
            ...((settings.galleryProps as unknown as Record<string, unknown>) || {}),
            ...createGridFunctions(settings.gridStyle),
        } as unknown,
    } as LooseDesignSettings;
};

/**
 * Normalize externally-provided metadata_json (e.g. via postMessage).
 */
const normalizeMetadataJson = (
    metadata: string | Record<string, unknown> | null | undefined
): LooseMetadataJson | null => {
    if (!metadata) return null;
    if (typeof metadata === "string") {
        return safeJsonParse<LooseMetadataJson>(metadata);
    }
    return metadata as LooseMetadataJson;
};

/**
 * Normalize externally-provided designDetails (e.g. via postMessage).
 */
const normalizeDesignDetails = (
    details: string | LooseDesignDetails | null | undefined
): LooseDesignDetails | null => {
    if (!details) return null;
    if (typeof details === "string") {
        return safeJsonParse<LooseDesignDetails>(details);
    }
    return details as LooseDesignDetails;
};

const getApiDesignSettings = (eventData: EventData | undefined): LooseDesignSettings | null => {
    const galleryStyle = eventData?.gallery_style;
    if (!galleryStyle) return null;

    const style = galleryStyle.style ?? basic.style;
    const v1 = style?.designSettings?.v1;
    if (!v1) return null;

    const merged = {
        ...v1,
        ...(eventData?.metadata_json ?? {}),
    } as LooseDesignSettings;

    return withGridFunctions(merged);
};

const getApiDesignDetails = (eventData: EventData | undefined): LooseDesignDetails | null => {
    const galleryStyle = eventData?.gallery_style;
    const style = galleryStyle?.style;
    if (!galleryStyle || !style) return null;

    return {
        name: galleryStyle.name,
        type: style.type,
        thumbnail: style.thumbnail,
        category: galleryStyle.category,
        is_global: galleryStyle.is_global,
        description: (galleryStyle as unknown as { description?: string })?.description,
    };
};

const getGalleryMode = (publishStatus: number | null | undefined): number =>
    publishStatus === null || publishStatus === undefined
        ? GALLERY_MODE.REGISTRATION
        : publishStatus === 0 || publishStatus === 1
            ? GALLERY_MODE.REGISTRATION
            : GALLERY_MODE.GALLERY;

const getIsGalleryExpired = (eventData: EventData | undefined): boolean => {
    if (eventData?.is_expired) return true;
    const validTillTimestamp = eventData?.event_valid_till;
    if (!validTillTimestamp) return false;

    const validTill =
        typeof validTillTimestamp === "string"
            ? parseInt(validTillTimestamp, 10)
            : validTillTimestamp;
    if (Number.isNaN(validTill)) return false;

    const currentTimestamp = Math.floor(Date.now() / 1000);
    return validTill + 100000 < currentTimestamp;
};

/**
 * Convert hex color to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    // Remove # if present
    hex = hex.replace("#", "");

    // Handle 3-digit hex
    if (hex.length === 3) {
        hex = hex.split("").map(char => char + char).join("");
    }

    if (hex.length !== 6) return null;

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
};

/**
 * Calculate relative luminance of a color
 * Uses the formula from WCAG 2.0: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
const getLuminance = (r: number, g: number, b: number): number => {
    const [rs, gs, bs] = [r, g, b].map(val => {
        val = val / 255;
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Determine if a color is dark or light
 * Returns "dark" if luminance < 0.5, "light" otherwise
 */
const isColorDark = (color: string): boolean => {
    if (!color) return false;

    // Handle hex colors
    if (color.startsWith("#")) {
        const rgb = hexToRgb(color);
        if (!rgb) return false;
        const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
        return luminance < 0.5;
    }

    // Handle rgb/rgba colors
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10);
        const g = parseInt(rgbMatch[2], 10);
        const b = parseInt(rgbMatch[3], 10);
        const luminance = getLuminance(r, g, b);
        return luminance < 0.5;
    }

    // Default to light for unknown formats
    return false;
};

/**
 * Apply CSS variables for color palette
 */
const applyColorPaletteStyles = (designSettings: LooseDesignSettings | null) => {
    if (!designSettings?.colorPaletteStyle) return;

    const body = document.body;
    body.setAttribute(
        "gallery-mode",
        designSettings.colorPaletteStyle["color-mode"] || "light"
    );

    Object.entries(designSettings.colorPaletteStyle).forEach(
        ([colorName, colorValue]) => {
            body.style.setProperty(`--gallery-${colorName}`, colorValue as string);
        }
    );

    // Set navbar theme attribute based on navbar background color
    // Check both the designSettings and computed style (in case CSS variable was set elsewhere)
    const navbarBgColorFromSettings = designSettings.colorPaletteStyle["navbar-background-color"];
    const navbarBgColorFromCSS = getComputedStyle(body).getPropertyValue("--gallery-navbar-background-color").trim();
    const navbarBgColor = navbarBgColorFromSettings || navbarBgColorFromCSS;

    if (navbarBgColor) {
        const isDark = isColorDark(navbarBgColor as string);
        body.setAttribute("data-navbar-theme", isDark ? "dark" : "light");
    } else {
        // Default to light if no navbar background color is set
        body.setAttribute("data-navbar-theme", "light");
    }

    // Set footer theme attribute based on footer background color
    // Check both the designSettings and computed style (in case CSS variable was set elsewhere)
    const footerBgColorFromSettings = designSettings.colorPaletteStyle["footer-background-color"];
    const footerBgColorFromCSS = getComputedStyle(body).getPropertyValue("--gallery-footer-background-color").trim();
    const footerBgColor = footerBgColorFromSettings || footerBgColorFromCSS;

    if (footerBgColor) {
        const isDark = isColorDark(footerBgColor as string);
        body.setAttribute("data-footer-theme", isDark ? "dark" : "light");
    } else {
        // Default to light if no footer background color is set
        body.setAttribute("data-footer-theme", "light");
    }

    // Set primary theme attribute based on primary background color
    // Check both the designSettings and computed style (in case CSS variable was set elsewhere)
    const primaryBgColorFromSettings = designSettings.colorPaletteStyle["primary-color"];
    const primaryBgColorFromCSS = getComputedStyle(body).getPropertyValue("--gallery-primary-color").trim();
    const primaryBgColor = primaryBgColorFromSettings || primaryBgColorFromCSS;

    if (primaryBgColor) {
        const isDark = isColorDark(primaryBgColor as string);
        body.setAttribute("data-primary-theme", isDark ? "dark" : "light");
    } else {
        // Default to light if no primary background color is set
        body.setAttribute("data-primary-theme", "light");
    }
};

/**
 * Apply CSS variables for gallery props
 */
const applyGalleryPropsStyles = (designSettings: LooseDesignSettings | null) => {
    if (!designSettings?.galleryProps) return;

    const body = document.body;
    body.style.setProperty(
        "--gallery-side-padding",
        `${designSettings.galleryProps.sidePadding}%`
    );
    body.style.setProperty(
        "--gallery-image-radius",
        `${designSettings.galleryProps.radius}px`
    );
};

/**
 * Apply CSS variables for branding style
 */
const applyBrandingStyles = (designSettings: LooseDesignSettings | null) => {
    if (!designSettings?.brandingStyle) return;

    const body = document.body;
    const cover = designSettings.brandingStyle.cover;
    const footer = designSettings.brandingStyle.footer;

    if (cover) {
        body.style.setProperty("--cover-logo-size", `${cover.logoSize}vh`);
        body.style.setProperty(
            "--show-cover-inquiry",
            cover.showInquiry ? "flex" : "none"
        );
    }

    if (footer) {
        body.style.setProperty("--footer-logo-size", `${footer.logoSize}vh`);
    }
};

/**
 * Apply typography (cover text style)
 */
const applyCoverTextStyles = (designSettings: LooseDesignSettings | null) => {
    if (!designSettings?.coverTextStyle) return;

    const body = document.body;
    const coverEventFont = designSettings.coverTextStyle["cover-event-font"];

    if (coverEventFont) {
        WebFont.load({
            google: {
                families: [coverEventFont],
            },
        });
    }

    Object.entries(designSettings.coverTextStyle).forEach(([name, value]) => {
        body.style.setProperty(`--gallery-${name}`, value as string);
    });
};

/**
 * Cleanup CSS variables (called on unmount or when designSettings becomes null)
 */
const cleanupCSSVariables = () => {
    const body = document.body;
    body.setAttribute("data-layout-mode", "light");
    body.removeAttribute("data-navbar-theme");
    body.removeAttribute("data-footer-theme");
    body.removeAttribute("data-primary-theme");
};

/**
 * Custom hook to fetch event data using GET_EVENT_OPEN endpoint
 * Automatically parses and stores designSettings in the event store
 * Also applies CSS variables based on designSettings
 * @param eventId - The event ID to fetch data for
 * @param key - Optional event key (pass_key/share_key) for authentication
 * @param options - Optional SWR configuration options
 * @returns Object containing event data, error, loading states, and mutate function
 */
export const useEventData = (
    eventId: string | number | null | undefined,
    key?: string | null,
    options?: {
        shouldRetryOnError?: boolean;
        revalidateOnFocus?: boolean;
        revalidateOnReconnect?: boolean;
    }
): UseEventDataReturn => {
    const {
        setEventId,
        setEventData,
        setDesignSettings,
        setMetadataJson,
        setDesignDetails,
        setPublishStatus,
        setGalleryMode,
        setIsGalleryExpired,
        setIsDesignPreview,
        setBearerToken,
    } = useEventStore();
    const [preview, setPreview] = useState<PreviewState | null>(null);

    const {
        data: response,
        error,
        isValidating,
        mutate,
    } = useSWR(
        eventId ? [GET_EVENT_OPEN, eventId, key] : null,
        () => getOpenEvent({ params: { event_id: eventId, ...(key ? { key } : {}) } }),
        {
            shouldRetryOnError: options?.shouldRetryOnError ?? false,
            // revalidateOnFocus: options?.revalidateOnFocus ?? true,
            revalidateOnReconnect: options?.revalidateOnReconnect ?? true,
        }
    );
    const isLoading = !response && !error;

    // Extract the data from the API response
    const eventData: EventData | undefined = response?.ok ? response.data : undefined;

    const apiBundle = useMemo(() => {
        const designSettings = getApiDesignSettings(eventData);
        return {
            designSettings,
            metadataJson: (eventData?.metadata_json ?? null) as LooseMetadataJson | null,
            designDetails: getApiDesignDetails(eventData),
            publishStatus: eventData?.publish_status ?? null,
            galleryMode: getGalleryMode(eventData?.publish_status),
            isGalleryExpired: getIsGalleryExpired(eventData),
        };
    }, [eventData]);

    const previewDesignSettings = preview?.designSettings ?? null;
    const previewDesignSettingsWithGrid = useMemo(
        () => (previewDesignSettings ? withGridFunctions(previewDesignSettings) : null),
        [previewDesignSettings]
    );

    const effectiveDesignSettings =
        previewDesignSettingsWithGrid ?? apiBundle.designSettings;
    const effectiveMetadataJson = preview?.metadataJson ?? apiBundle.metadataJson;
    const effectiveDesignDetails = preview?.designDetails ?? apiBundle.designDetails;
    const isDesignPreview = !!preview?.designSettings;
    const effectiveBearerToken = preview?.bearerToken ?? null;

    // Keep zustand store in sync (single effect, clear precedence: preview -> api)
    useEffect(() => {
        if (eventData) {
            setEventId(eventId?.toString() || null);
            setEventData(eventData);
            setPublishStatus(apiBundle.publishStatus);
            setGalleryMode(apiBundle.galleryMode);
            setIsGalleryExpired(apiBundle.isGalleryExpired);
        } else {
            // Reset when eventData is null/undefined
            setIsGalleryExpired(false);
        }
        setDesignSettings(effectiveDesignSettings);
        setMetadataJson(effectiveMetadataJson);
        setDesignDetails(effectiveDesignDetails);
        setIsDesignPreview(isDesignPreview);
        setBearerToken(effectiveBearerToken);
    }, [
        apiBundle.galleryMode,
        apiBundle.isGalleryExpired,
        apiBundle.publishStatus,
        effectiveBearerToken,
        effectiveDesignDetails,
        effectiveDesignSettings,
        effectiveMetadataJson,
        eventData,
        eventId,
        isDesignPreview,
        setBearerToken,
        setDesignDetails,
        setDesignSettings,
        setEventData,
        setEventId,
        setGalleryMode,
        setIsDesignPreview,
        setIsGalleryExpired,
        setMetadataJson,
        setPublishStatus,
    ]);

    // Listen for postMessage preview updates (iframe -> parent or parent -> iframe on same origin)
    useEffect(() => {
        const handleMessage = (event: MessageEvent<DesignSettingsMessage>) => {
            // Expect same-origin messages (sender uses window.location.origin as target origin)
            // if (event.origin !== window.location.origin) return;

            const data = event.data;
            if (!data || data.type !== "DESIGN_SETTINGS") return;
            const msg = data as Extract<DesignSettingsMessage, { type: "DESIGN_SETTINGS" }>;

            // Allow clearing preview: { type: "DESIGN_SETTINGS", designSettings: null }
            if (!msg.designSettings) {
                setPreview(null);
                return;
            }

            try {
                const parsedDesignSettings =
                    typeof msg.designSettings === "string"
                        ? safeJsonParse<LooseDesignSettings>(msg.designSettings)
                        : (msg.designSettings as LooseDesignSettings);

                const parsedMetaData = normalizeMetadataJson(msg.eventMetaData);
                const parsedDesignDetails = normalizeDesignDetails(msg.designDetails);

                setPreview({
                    designSettings: parsedDesignSettings,
                    metadataJson: parsedMetaData,
                    designDetails: parsedDesignDetails,
                    bearerToken: msg.bearerToken ?? null,
                });
            } catch (e) {
                console.error("[useEventData] Error processing postMessage designSettings", e);
                Sentry.captureException(e);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // Apply CSS variables when designSettings changes
    useEffect(() => {
        if (!effectiveDesignSettings) {
            cleanupCSSVariables();
            return;
        }

        applyColorPaletteStyles(effectiveDesignSettings);
        applyGalleryPropsStyles(effectiveDesignSettings);
        applyBrandingStyles(effectiveDesignSettings);
        applyCoverTextStyles(effectiveDesignSettings);

        return () => {
            cleanupCSSVariables();
        };
    }, [effectiveDesignSettings]);

    return {
        data: eventData,
        error,
        isLoading,
        isValidating,
        mutate,
    };
};

